import { useEffect, useLayoutEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  CloseIcon,
  cn,
  Field,
  FormLabel,
  Heading,
  Label,
  MinusIcon,
  Paragraph,
  PlusIcon,
  Popup,
  Select,
  showToast,
  Textarea,
  TriangleExclamationIcon,
} from '@quickmd-llc/design-system'
import {
  ADDITIONAL_INFORMATION_TEXT,
  CANCEL_PLAN_CONFIRM_TITLE_TEXT,
  CANCEL_PLAN_TEXT,
  CANCEL_TEXT,
  EDIT_PLAN_VISITS_TEXT,
  GO_BACK_TEXT,
  NEW_BALANCE_TEXT,
  NUMBER_OF_VISITS_TEXT,
  PLAN_CANCELLED_TEXT,
  PLAN_EXPIRES_TEXT,
  PLAN_PURCHASED_TEXT,
  PLAN_REMAINING_TEXT,
  PLAN_VISITS_UPDATED_TEXT,
  REASON_FOR_ADJUSTMENT_TEXT,
  REASON_FOR_CANCELLATION_TEXT,
  SELECT_REASON_TEXT,
  TYPE_SOMETHING_TEXT,
  UPDATE_VISITS_TEXT,
} from '@/utils/constants'
import {
  useDeletePatientsPatientIdPackagesPatientPackageId,
  usePutPatientsPatientIdPackagesPatientPackageIdVisits,
  getGetPatientsPatientIdPackagesQueryKey,
} from '@/api/generated/package/package'
import { useGetLookups } from '@/api/generated/lookup/lookup'
import {
  type CreditReasonLookupItemOutContract,
  LookupType,
  type PatientPackageApiOutContract,
} from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { formatPlanDate, getPlanVisitBalance } from '@/utils/programPlanVisits'
import { getEditPlanVisitsErrorMessage } from './getEditPlanVisitsErrorMessage'

type Props = {
  isOpen: boolean
  patientId: string
  /** The active plan, or `null` when the patient holds none. The modal stays mounted
   *  regardless (DS Popup requirement) and simply reads closed when there is no plan. */
  programPackage: PatientPackageApiOutContract | null
  onClose: () => void
}

type View = 'edit' | 'confirm-cancel'
type ReasonOption = { value: string; label: string }

/**
 * "Edit plan visits" — the admin surface for replenishing (or reducing) a
 * patient's Quit 7-OH Plan visit balance, and for cancelling the plan (QMD-4371).
 *
 * The stepper picks a signed delta clamped so the new balance can never fall
 * below 0 or exceed what the plan was sold with. Cancelling swaps the whole modal
 * to a distinct confirmation dialog with an optional free-text reason. On success
 * we invalidate the packages query so the balance shows the latest; the audit log
 * is left for staff to refresh manually, to avoid an extra call on every edit.
 */
export const EditPlanVisitsModal = ({ isOpen, patientId, programPackage, onClose }: Props) => {
  const queryClient = useQueryClient()

  const balance = getPlanVisitBalance(programPackage)
  const remaining = balance?.remaining ?? 0
  const allowed = balance?.allowed ?? 0
  const purchased = balance?.purchased ?? 0
  const visitTypeId = balance?.visitTypeId ?? ''
  const patientPackageId = programPackage?.Id ?? ''

  // Add is capped at one purchased set per session (maxDelta = purchased) so an admin can't grant an
  // unbounded count in a single edit — to add more, they reopen and add another set. Reduce can undo
  // this session's additions and remove visits granted beyond what the patient purchased, but never
  // drop below the purchased count: minDelta = purchased - allowed (0 when nothing was granted,
  // negative by the number of granted extras otherwise).
  const minDelta = purchased - allowed
  const maxDelta = purchased

  const [view, setView] = useState<View>('edit')
  // Start at 0 (no change) so the balance is untouched until staff explicitly step it.
  const [delta, setDelta] = useState(0)
  const [adjustReason, setAdjustReason] = useState('')
  const [adjustComment, setAdjustComment] = useState('')
  const [cancelReason, setCancelReason] = useState('')

  const { mutate: adjustVisits, isPending: isAdjusting } = usePutPatientsPatientIdPackagesPatientPackageIdVisits()
  const { mutate: cancelPlan, isPending: isCancelling } = useDeletePatientsPatientIdPackagesPatientPackageId()
  const isPending = isAdjusting || isCancelling

  // Adjustment reasons come from the packageupdatereasons lookup; the value sent as the
  // free-text `Reason` is the reason's own text. Cached for the session (staleTime/gcTime
  // Infinity) so it is fetched at most once per login rather than on every open.
  const modalActive = isOpen && Boolean(programPackage)
  const { data: updateReasons = [], isLoading: updateReasonsLoading } = useGetLookups<
    CreditReasonLookupItemOutContract[]
  >(
    { type: LookupType.packageupdatereasons },
    { query: { enabled: modalActive && view === 'edit', staleTime: Infinity, gcTime: Infinity } },
  )
  const adjustReasonOptions: ReasonOption[] = updateReasons
    .filter((reason) => Boolean(reason.Text))
    .map((reason) => ({ value: reason.Text ?? '', label: reason.Text ?? '' }))

  // DS Popup renders into a portal outside .tailwind-scoped — add the class early so the
  // Tailwind utility classes in the body resolve (mirrors EditCreditModal).
  useLayoutEffect(() => {
    const portalRoot = document.getElementById('headlessui-portal-root')
    if (portalRoot) portalRoot.classList.add('tailwind-scoped')
  }, [])

  // DS Popup stays mounted; reset the form each time the modal opens so a reopen is a clean slate.
  useEffect(() => {
    if (isOpen) {
      setView('edit')
      setDelta(0)
      setAdjustReason('')
      setAdjustComment('')
      setCancelReason('')
    }
  }, [isOpen])

  // If the balance changes under us while the modal is open (e.g. a background packages
  // refetch), the clamp bounds move — re-clamp delta so it can never fall out of range and
  // submit an out-of-bounds adjustment.
  useEffect(() => {
    setDelta((d) => Math.min(maxDelta, Math.max(minDelta, d)))
  }, [minDelta, maxDelta])

  const newBalance = remaining + delta
  const canSubmit = delta !== 0 && !!adjustReason && !isPending
  const isModalOpen = isOpen && Boolean(programPackage)

  // Refresh the plan row (packages query) so the balance shows the latest. The audit log is
  // deliberately NOT refetched here — staff can refresh it manually, and skipping it avoids an
  // extra API call on every edit.
  const refreshPackageInfo = () => {
    void queryClient.invalidateQueries({ queryKey: getGetPatientsPatientIdPackagesQueryKey(patientId) })
  }

  const handleError = (error: unknown) => {
    captureException(error)
    showToast({ type: 'error', message: getEditPlanVisitsErrorMessage(error), position: 'bottom-right' })
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    adjustVisits(
      {
        patientId,
        patientPackageId,
        data: {
          Adjustments: [{ VisitTypeId: visitTypeId, VisitCount: delta }],
          Reason: adjustReason,
          Comment: adjustComment.trim() || null,
        },
      },
      {
        onSuccess: () => {
          refreshPackageInfo()
          showToast({ type: 'success', message: PLAN_VISITS_UPDATED_TEXT, position: 'bottom-right' })
          onClose()
        },
        onError: handleError,
      },
    )
  }

  const handleConfirmCancel = () => {
    if (isCancelling) return
    cancelPlan(
      {
        patientId,
        patientPackageId,
        data: { Reason: cancelReason.trim() || null },
      },
      {
        onSuccess: () => {
          refreshPackageInfo()
          showToast({ type: 'success', message: PLAN_CANCELLED_TEXT, position: 'bottom-right' })
          onClose()
        },
        onError: handleError,
      },
    )
  }

  const summaryCard = (
    <div className='flex flex-col gap-sp-1.5 rounded-large border border-border-highlight bg-[#F4F8FA] p-sp-2'>
      <div className='flex items-center justify-between'>
        <Label size='md'>{programPackage?.Name}</Label>
        <button
          type='button'
          onClick={() => setView('confirm-cancel')}
          disabled={isPending}
          className='font-semibold text-error disabled:cursor-not-allowed disabled:opacity-40'>
          {CANCEL_PLAN_TEXT}
        </button>
      </div>
      <div className='flex justify-between gap-sp-2 border-t border-border-highlight pt-sp-1.5'>
        <div className='flex flex-col items-start gap-sp-0.25'>
          <Label size='sm' className='uppercase text-text-light'>
            {PLAN_PURCHASED_TEXT}
          </Label>
          <Paragraph size='md' className='text-text-dark'>
            {formatPlanDate(programPackage?.PurchasedDateTimeUtc)}
          </Paragraph>
        </div>
        <div className='flex flex-col items-center gap-sp-0.25'>
          <Label size='sm' className='uppercase text-text-light'>
            {PLAN_EXPIRES_TEXT}
          </Label>
          <Paragraph size='md' className='text-text-dark'>
            {formatPlanDate(programPackage?.ValidUntilDateTimeUtc)}
          </Paragraph>
        </div>
        <div className='flex flex-col items-end gap-sp-0.25'>
          <Label size='sm' className='uppercase text-text-light'>
            {PLAN_REMAINING_TEXT}
          </Label>
          <Label size='md' className='text-text-highlight'>
            {remaining}
          </Label>
        </div>
      </div>
    </div>
  )

  const editBody = (
    <div className='flex flex-col gap-sp-2.5'>
      {summaryCard}

      <div className='flex items-start gap-sp-1.5'>
        {/* Fixed stepper-column width (Figma: 194px, 12px gap) so the New balance section
            starts far enough right to match the design. */}
        <div className='w-[194px]'>
          <Field>
            <div className='flex items-end'>
              <FormLabel size='md' className='mb-0'>
                {NUMBER_OF_VISITS_TEXT}
              </FormLabel>
            </div>
            <div className='mt-2 flex items-center gap-sp-1'>
              <button
                type='button'
                aria-label='Remove a visit'
                disabled={delta <= minDelta || isPending}
                onClick={() => setDelta((d) => Math.max(minDelta, d - 1))}
                className='flex h-10 w-10 items-center justify-center rounded-full border border-border-highlight text-text-highlight disabled:cursor-not-allowed disabled:opacity-40'>
                <MinusIcon iconSize='s' />
              </button>
              <span className='min-w-[24px] text-center text-lg text-text-dark'>{delta}</span>
              <button
                type='button'
                aria-label='Add a visit'
                disabled={delta >= maxDelta || isPending}
                onClick={() => setDelta((d) => Math.min(maxDelta, d + 1))}
                className='flex h-10 w-10 items-center justify-center rounded-full bg-highlight text-text-inverse disabled:cursor-not-allowed disabled:opacity-40'>
                <PlusIcon iconSize='s' />
              </button>
            </div>
          </Field>
        </div>

        <div className='flex-1'>
          <Field>
            <div className='flex items-end'>
              <FormLabel size='md' className='mb-0'>
                {NEW_BALANCE_TEXT}
              </FormLabel>
            </div>
            <div
              className={cn(
                'mt-2 flex h-11 items-center rounded-large border border-border-medium px-[14px]',
                delta === 0 ? 'bg-surface-disabled' : 'bg-[#F4F8FA]',
              )}>
              <Paragraph size='md' className={delta === 0 ? 'text-text-light' : 'text-text-dark'}>
                {newBalance} remaining
              </Paragraph>
            </div>
          </Field>
        </div>
      </div>

      <Field>
        <FormLabel size='md'>{REASON_FOR_ADJUSTMENT_TEXT}</FormLabel>
        <Select
          value={adjustReason}
          disabled={isPending || updateReasonsLoading}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAdjustReason(e.target.value)}>
          <option value=''>{SELECT_REASON_TEXT}</option>
          {adjustReasonOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <FormLabel size='md'>{ADDITIONAL_INFORMATION_TEXT}</FormLabel>
        <Textarea
          className='resize-y'
          placeholder={TYPE_SOMETHING_TEXT}
          value={adjustComment}
          disabled={isPending}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdjustComment(e.target.value)}
        />
      </Field>
    </div>
  )

  const confirmBody = (
    <div className='flex flex-col gap-sp-3'>
      <div className='flex items-center gap-sp-2'>
        <TriangleExclamationIcon iconSize='xl' className='text-error' />
        <Heading level='h2'>{CANCEL_PLAN_CONFIRM_TITLE_TEXT}</Heading>
      </div>
      <Paragraph size='sm' className='text-text-medium'>
        {`Cancels the ${programPackage?.Name ?? 'plan'} immediately and returns the patient to pay-as-you-go. Remaining visits can no longer be used, and no refund is issued automatically. This action can't be undone.`}
      </Paragraph>
      <Field>
        <FormLabel size='md'>{REASON_FOR_CANCELLATION_TEXT}</FormLabel>
        <Textarea
          className='resize-y'
          placeholder={TYPE_SOMETHING_TEXT}
          value={cancelReason}
          disabled={isCancelling}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCancelReason(e.target.value)}
        />
      </Field>
    </div>
  )

  const editFooter = (
    <div className='flex justify-end gap-sp-1.5'>
      <Button priority='secondary' onClick={onClose} disabled={isPending} size='m'>
        {CANCEL_TEXT}
      </Button>
      <Button onClick={handleSubmit} loading={isAdjusting} disabled={!canSubmit} size='m'>
        {UPDATE_VISITS_TEXT}
      </Button>
    </div>
  )

  const confirmFooter = (
    <div className='flex justify-end gap-sp-1.5'>
      <Button priority='secondary' onClick={() => setView('edit')} disabled={isCancelling} size='m'>
        {GO_BACK_TEXT}
      </Button>
      <Button theme='danger' onClick={handleConfirmCancel} loading={isCancelling} size='m'>
        {CANCEL_PLAN_TEXT}
      </Button>
    </div>
  )

  const isEdit = view === 'edit'

  return (
    <Popup
      isOpen={isModalOpen}
      showCloseIcon={isEdit}
      header={
        isEdit ?
          { title: EDIT_PLAN_VISITS_TEXT, closeIcon: <CloseIcon iconSize='m' className='text-text-dark' /> }
        : undefined
      }
      body={isEdit ? editBody : confirmBody}
      footer={isEdit ? editFooter : confirmFooter}
      onClose={isEdit ? onClose : () => setView('edit')}
      styles={{ containerClassName: 'w-[480px]' }}
    />
  )
}
